import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { fireAdsConversion } from "../lib/analytics";
import WorkspaceBanner from "../components/WorkspaceBanner";
import { GOOGLE_WORKSPACE_REFERRAL_URL, trackWorkspaceReferralClick } from "../lib/partners";
import { toast } from "sonner";
import { Plus, Globe2, Mail, Trash2, CheckCircle2, AlertCircle, Crown, ExternalLink, X, Sparkles, Zap, ChevronDown, Copy, Users as UsersIcon } from "lucide-react";
import FindDomainCard from "../components/FindDomainCard";
import RestoreFromGmailCard from "../components/RestoreFromGmailCard";
import SendAsModal from "../components/SendAsModal";
import AliasStats from "../components/AliasStats";
import DeliverabilityBadge from "../components/DeliverabilityBadge";
import InviteTeammateModal from "../components/InviteTeammateModal";
import DomainPlanPickerModal from "../components/DomainPlanPickerModal";

const PLAN_BADGE = {
  starter: "bg-slate-100 text-slate-700",
  growth: "bg-[#FEE7E1] text-[#F26953]",
  pro: "bg-[#0F172A] text-white",
};

function AliasModal({ open, onClose, domains, plan, aliasCount, onCreated }) {
  const [domainId, setDomainId] = useState("");
  const [alias, setAlias] = useState("");
  const [destinations, setDestinations] = useState([""]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDomainId(domains[0]?.id || "");
      setAlias("");
      setDestinations([""]);
    }
  }, [open, domains]);

  if (!open) return null;

  // Limits follow the SELECTED DOMAIN's tier (each domain can have its own plan).
  const selectedDomain = domains.find((d) => d.id === domainId);
  const selectedTier = selectedDomain?.plan_tier || plan || "starter";
  const TIER_MAX_DESTS = { starter: 1, growth: 3, pro: Infinity };
  const maxDests = plan === "admin" ? Infinity : (TIER_MAX_DESTS[selectedTier] ?? 1);
  const canAddMore = destinations.length < maxDests;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dests = destinations.map(d => d.trim()).filter(Boolean);
      const { data } = await api.post("/aliases", { domain_id: domainId, alias: alias.trim(), destinations: dests });
      toast.success(`Alias ${data.alias}@${data.domain} created`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4" data-testid="alias-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white border border-[#E2E8F0] shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">Create email alias</h3>
            <p className="text-xs text-[#475569]">Forward incoming mail to your existing inbox.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="alias-modal-close"><X className="h-4 w-4"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="alias-form">
          <label className="block">
            <span className="text-sm font-medium text-[#0F172A]">Domain</span>
            <select value={domainId} onChange={(e) => setDomainId(e.target.value)} required
              data-testid="alias-domain-select"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] outline-none focus:border-[#F26953] text-sm">
              {domains.map(d => <option key={d.id} value={d.id}>{d.domain}{d.plan_tier ? ` · ${d.plan_tier.charAt(0).toUpperCase()}${d.plan_tier.slice(1)}` : ""}</option>)}
            </select>
            {selectedDomain && selectedDomain.plan_tier && plan !== "admin" && (
              <div className="mt-1 text-[11px] text-slate-500" data-testid="alias-domain-tier-hint">
                This domain is on the <span className="font-semibold capitalize">{selectedDomain.plan_tier}</span> tier.
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-sm font-medium text-[#0F172A]">Alias</span>
            <div className="mt-1.5 flex items-center rounded-lg border border-[#E2E8F0] focus-within:border-[#F26953] overflow-hidden">
              <input value={alias} onChange={(e) => setAlias(e.target.value)} required placeholder="hello"
                data-testid="alias-name-input"
                className="flex-1 px-3 py-2.5 text-sm outline-none"/>
              <span className="px-3 text-sm text-slate-500 bg-slate-50 border-l border-[#E2E8F0]">@{domains.find(d => d.id === domainId)?.domain || "yourdomain.com"}</span>
            </div>
          </label>
          <div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#0F172A]">Forward to</span>
              {canAddMore && (
                <button type="button" onClick={() => setDestinations([...destinations, ""])}
                  data-testid="alias-add-recipient-btn"
                  className="text-xs text-[#F26953] font-semibold hover:underline">+ Add recipient</button>
              )}
            </div>
            <div className="mt-1.5 space-y-2">
              {destinations.map((d, i) => (
                <div key={i} className="flex gap-2">
                  <input type="email" required value={d} placeholder="you@yourmail.com"
                    onChange={(e) => setDestinations(destinations.map((x, idx) => idx === i ? e.target.value : x))}
                    data-testid={`alias-destination-${i}`}
                    className="flex-1 px-3 py-2.5 rounded-lg border border-[#E2E8F0] outline-none focus:border-[#F26953] text-sm"/>
                  {destinations.length > 1 && (
                    <button type="button" onClick={() => setDestinations(destinations.filter((_, idx) => idx !== i))}
                      className="p-2 rounded-lg hover:bg-slate-100">
                      <X className="h-4 w-4 text-slate-500"/>
                    </button>
                  )}
                </div>
              ))}
            </div>
            {maxDests === 1 && (
              <p className="text-xs text-slate-400 mt-2" data-testid="alias-multi-recipient-hint">
                This domain's tier only forwards to 1 recipient. Upgrade this domain to Growth (3) or Pro (unlimited).
              </p>
            )}
            {Number.isFinite(maxDests) && maxDests > 1 && (
              <p className="text-xs text-slate-400 mt-2" data-testid="alias-multi-recipient-hint">
                This domain allows up to {maxDests} recipients per alias. Upgrade it to Pro for unlimited fan-out.
              </p>
            )}
          </div>
          {/* Workspace nudge — appears when creating the 6th+ alias. This
              signals the user has real inbox traffic (not just parking mail),
              which is exactly when a full Gmail mailbox on their domain
              starts making sense. High-intent moment for the referral. */}
          {aliasCount >= 5 && (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50 p-3.5"
              data-testid="alias-modal-workspace-nudge"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-amber-900">
                    Getting real inbox traffic? Consider Google Workspace.
                  </div>
                  <div className="mt-1 text-[11px] text-amber-800/80 leading-relaxed">
                    With {aliasCount} aliases already forwarding, a full Gmail mailbox on your own domain
                    (plus Drive, Docs and Meet for your team) may be a better fit.
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <a
                      href={GOOGLE_WORKSPACE_REFERRAL_URL}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      onClick={() => trackWorkspaceReferralClick("alias-modal-nudge")}
                      data-testid="alias-modal-workspace-cta"
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-900 hover:text-amber-700 underline decoration-amber-500/40 underline-offset-2"
                    >
                      Try free for 14 days
                    </a>
                    <Link
                      to="/workspace-upgrade"
                      onClick={onClose}
                      data-testid="alias-modal-workspace-details"
                      className="text-[11px] text-amber-800/70 hover:text-amber-700"
                    >
                      Compare features →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
          <button type="submit" disabled={submitting} data-testid="alias-submit-btn"
            className="w-full bg-[#F26953] text-white font-semibold py-2.5 rounded-lg hover:bg-[#D85340] disabled:opacity-60">
            {submitting ? "Creating…" : "Create alias"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, config, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [domains, setDomains] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aliasModal, setAliasModal] = useState(false);
  const [sendAsAlias, setSendAsAlias] = useState(null);
  const [inviteModal, setInviteModal] = useState(false);
  const [extraDomainModal, setExtraDomainModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([api.get("/domains"), api.get("/aliases")]);
      setDomains(d.data); setAliases(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle returning from Stripe Checkout (?session_id=...)
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !user || typeof user !== "object") return;
    let attempts = 0;
    let stopped = false;
    const fireConversion = () => {
      // Subscription purchase return — convert plan key to value and call shared util.
      const planFull = params.get("plan") || "starter_monthly";
      const planId = planFull.split("_")[0];
      const cycle = planFull.split("_")[1] || "monthly";
      // Per-plan values in USD. Update if you re-price.
      // Annual = full year amount (Starter $30/yr, Growth $60/yr, Pro $90/yr).
      const VALUES = {
        starter_monthly: 3, growth_monthly: 6, pro_monthly: 9,
        starter_annual: 30, growth_annual: 60, pro_annual: 90,
      };
      const value = VALUES[planFull] ?? 3;
      const planName = planId.charAt(0).toUpperCase() + planId.slice(1);
      fireAdsConversion({
        transaction_id: sid,
        value,
        item_id: planFull,
        item_name: `oly Inbox ${planName} (${cycle})`,
      });
    };
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          fireConversion();           // fire BEFORE URL clean so analytics always wins the race
          toast.success("Payment confirmed — your plan is updated.");
          await refresh();
          load();
          params.delete("session_id"); params.delete("plan"); setParams(params, { replace: true });
          // If they just bought a per-tier extra-domain slot, take them straight
          // into the add-domain wizard with the tier pre-selected.
          if (data.kind === "extra_domain_tier" && data.tier) {
            nav(`/dashboard/add-domain?tier=${data.tier}`);
          } else if (data.kind === "extra_domain_slot") {
            nav(`/dashboard/add-domain?tier=starter`);
          }
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          params.delete("session_id"); params.delete("plan"); setParams(params, { replace: true });
          return;
        }
        if (++attempts < 6) setTimeout(poll, 2000);
        else {
          toast("Payment is still processing. We'll update your plan shortly.", { icon: "⏳" });
          params.delete("session_id"); params.delete("plan"); setParams(params, { replace: true });
        }
      } catch (e) {
        toast.error(formatApiError(e));
        params.delete("session_id"); params.delete("plan"); setParams(params, { replace: true });
      }
    };
    toast.loading("Confirming payment…", { id: "pay-toast", duration: 1500 });
    poll();
    return () => { stopped = true; };
  }, [params.get("session_id")]);

  // Legacy: direct plan param (admin/demo signup flow). MUST NOT fire when
  // ?session_id= is also present — that's the Stripe checkout return path,
  // which is handled by the effect above (via /billing/status polling +
  // webhook-applied plan update). Without this guard the legacy code would
  // try to "shortcut" the plan update using the *catalog key* (e.g.
  // "starter_monthly") which the /billing/select-plan endpoint rejects with
  // 400 "Input should be 'free', 'starter', 'growth' or 'pro'" — and that
  // failure (+ its URL cleanup) was also stripping the session_id from the
  // URL before the Google Ads conversion could fire.
  useEffect(() => {
    if (params.get("session_id")) return;
    const planQ = params.get("plan");
    const cycleQ = params.get("cycle");
    if (planQ && user && typeof user === "object" && user.role === "admin") {
      api.post("/billing/select-plan", { plan: planQ, billing_cycle: cycleQ || "monthly" })
        .then(() => { toast.success(`Plan updated to ${planQ}`); refresh(); })
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => { params.delete("plan"); params.delete("cycle"); setParams(params, { replace: true }); });
    }
  }, [params, user, refresh, setParams]);

  const deleteDomain = async (id) => {
    if (!window.confirm("Delete this domain and all its aliases?")) return;
    try { await api.delete(`/domains/${id}`); toast.success("Domain deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteAlias = async (id) => {
    if (!window.confirm("Delete this alias?")) return;
    try { await api.delete(`/aliases/${id}`); toast.success("Alias deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const verifyDomain = async (id) => {
    try {
      await api.post(`/domains/${id}/verify`);
      toast.success("Domain verified ✓");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user || typeof user !== "object") return null;
  const planLabel = user.role === "admin" ? "admin" : user.plan;
  const planConf = config?.plans?.find(p => p.id === user.plan);
  const slots = user.extra_domain_slots || { starter: 0, growth: 0, pro: 0 };
  const extraDomains = (slots.starter || 0) + (slots.growth || 0) + (slots.pro || 0) || (user.extra_domains || 0);
  const basePlanDomains = planConf?.max_domains ?? 1;
  const totalDomainLimit = user.role === "admin" ? "∞" : (basePlanDomains + extraDomains);
  const limitAliasesPerDomain = user.role === "admin" ? "∞" : (planConf?.max_aliases ?? 1);
  // Capacity check runs per-tier on the backend, but for the "do we need to buy a new slot?"
  // UI decision we just check whether the user has any unfilled capacity at all.
  const atDomainLimit = user.role !== "admin" && domains.length >= basePlanDomains + extraDomains;

  const isFreePlan = user.role !== "admin" && user.plan === "free";

  const handleAddDomain = () => {
    if (isFreePlan) {
      toast.info("Choose a plan to activate your inbox.");
      nav("/#pricing");
      return;
    }
    if (atDomainLimit) { setExtraDomainModal(true); return; }
    nav("/dashboard/add-domain");
  };

  const onAdminSlotAdded = async (tier) => {
    await refresh();
    setExtraDomainModal(false);
    nav(`/dashboard/add-domain?tier=${tier || "starter"}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="dashboard-main">
        <WorkspaceBanner />
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight">Welcome back, {user.name || user.email.split("@")[0]}</h1>
            <p className="text-sm text-[#475569] mt-2">Manage your domains and email aliases.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider ${PLAN_BADGE[planLabel] || PLAN_BADGE.starter}`} data-testid="plan-badge">
              {user.role === "admin" && <Crown className="h-3 w-3"/>} {planLabel} {user.role !== "admin" && `· ${user.billing_cycle}`}
            </span>
            {user.role !== "admin" && (
              <>
                <Link to="/#pricing" data-testid="dashboard-upgrade-link" className="text-sm font-semibold text-[#F26953] hover:underline inline-flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5"/> Change plan
                </Link>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/dashboard" });
                      window.location.href = data.url;
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  data-testid="dashboard-billing-portal"
                  className="text-sm font-semibold text-[#0F172A] hover:bg-slate-100 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                >
                  Manage billing
                </button>
              </>
            )}
          </div>
        </div>

        {/* Free-plan gate — shown prominently at top until user picks a paid plan */}
        {!loading && isFreePlan && (
          <div data-testid="free-plan-gate" className="mb-10 rounded-2xl bg-gradient-to-br from-[#FFF1EC] to-white border border-[#F26953]/30 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#F26953] grid place-items-center flex-shrink-0">
                <Sparkles className="h-6 w-6 text-white"/>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] tracking-tight">Choose a plan to activate your inbox</h2>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">
                  You're signed in, but you'll need a paid plan before you can connect a domain or create an alias. Plans start at <strong>$3/mo</strong> — cancel anytime.
                </p>
                <button
                  onClick={() => nav("/#pricing")}
                  data-testid="free-plan-gate-cta"
                  className="mt-4 inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#D85340] transition-colors"
                >
                  See plans <Sparkles className="h-4 w-4"/>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Find me a domain — shown only when user has 0 domains AND has a paid plan */}
        {!loading && domains.length === 0 && user.role !== "admin" && !isFreePlan && <FindDomainCard />}

        {/* Restore from Gmail — shown when user has ≥1 domain AND signed up from a free provider */}
        {!loading && domains.length > 0 && user.role !== "admin" && (
          <RestoreFromGmailCard
            user={user}
            domains={domains}
            existingAliases={aliases}
            onCreated={load}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="rounded-xl bg-white border border-[#E2E8F0] p-5">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Domains</div>
            <div className="mt-2 text-3xl font-bold text-[#0F172A]">{domains.length} <span className="text-sm font-normal text-slate-400">/ {totalDomainLimit}</span></div>
            {extraDomains > 0 && user.role !== "admin" && (
              <div className="mt-1 text-xs text-[#F26953] font-semibold" data-testid="stat-extra-slots-breakdown">
                {(() => {
                  const parts = [];
                  if (slots.starter) parts.push(`${slots.starter} Starter`);
                  if (slots.growth)  parts.push(`${slots.growth} Growth`);
                  if (slots.pro)     parts.push(`${slots.pro} Pro`);
                  const monthly = (slots.starter || 0) * 3 + (slots.growth || 0) * 6 + (slots.pro || 0) * 9;
                  return parts.length ? `+${parts.join(" · ")} · $${monthly}/mo` : `+${extraDomains} extra · paid`;
                })()}
              </div>
            )}
          </div>
          <div className="rounded-xl bg-white border border-[#E2E8F0] p-5">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Aliases</div>
            <div className="mt-2 text-3xl font-bold text-[#0F172A]" data-testid="stat-aliases-count">{aliases.length}</div>
            <div className="mt-1 text-xs text-slate-400">Limit depends on each domain's tier</div>
          </div>
          <div className="rounded-xl bg-white border border-[#E2E8F0] p-5">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Verified</div>
            <div className="mt-2 text-3xl font-bold text-[#0F172A]">{domains.filter(d => d.verified).length}<span className="text-sm font-normal text-slate-400"> / {domains.length || 0}</span></div>
          </div>
        </div>

        {/* DOMAINS */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#0F172A] inline-flex items-center gap-2"><Globe2 className="h-5 w-5 text-[#F26953]"/> Domains</h2>
            <button onClick={handleAddDomain} data-testid="add-domain-btn"
              className="inline-flex items-center gap-2 bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-slate-800">
              <Plus className="h-4 w-4"/> Add domain
            </button>
          </div>
          <div className="rounded-2xl bg-white border border-[#E2E8F0] overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
            ) : domains.length === 0 ? (
              <div className="p-12 text-center">
                <Globe2 className="h-10 w-10 mx-auto text-slate-300"/>
                <p className="mt-3 text-sm text-[#475569]">No domains yet. Add your first one to start receiving email.</p>
                <button onClick={handleAddDomain} className="mt-4 bg-[#F26953] text-white font-semibold px-4 py-2 rounded-lg hover:bg-[#D85340] text-sm">Add domain</button>
                <div className="mt-6 text-xs text-slate-400">No domain yet? <a href={config?.registrar?.affiliate_url || config?.spaceship_affiliate_url} target="_blank" rel="noreferrer" className="text-[#F26953] font-semibold hover:underline inline-flex items-center gap-1">Get one on {config?.registrar?.name || "Dynadot"} <ExternalLink className="h-3 w-3"/></a></div>
              </div>
            ) : (
              <ul className="divide-y divide-[#E2E8F0]">
                {domains.map((d) => (
                  <DomainRow
                    key={d.id}
                    d={d}
                    isAdmin={user.role === "admin"}
                    onVerify={() => verifyDomain(d.id)}
                    onDelete={() => deleteDomain(d.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* ALIASES */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#0F172A] inline-flex items-center gap-2"><Mail className="h-5 w-5 text-[#F26953]"/> Aliases</h2>
            <button onClick={() => {
                if (isFreePlan) { toast.info("Choose a plan to activate your inbox."); nav("/#pricing"); return; }
                setAliasModal(true);
              }} disabled={domains.length === 0} data-testid="add-alias-btn"
              className="inline-flex items-center gap-2 bg-[#F26953] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#D85340] disabled:opacity-50">
              <Plus className="h-4 w-4"/> Add alias
            </button>
            {(user.role === "admin" || user.plan === "pro") && domains.some(d => d.verified) && (
              <button onClick={() => setInviteModal(true)} data-testid="invite-teammate-btn"
                className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] text-[#0F172A] text-sm font-semibold px-4 py-2 rounded-lg hover:bg-slate-50">
                <UsersIcon className="h-4 w-4"/> Invite teammate
              </button>
            )}
          </div>
          <div className="rounded-2xl bg-white border border-[#E2E8F0] overflow-hidden">
            {aliases.length === 0 ? (
              <div className="p-12 text-center">
                <Mail className="h-10 w-10 mx-auto text-slate-300"/>
                <p className="mt-3 text-sm text-[#475569]">No aliases yet. Add a domain first, then create aliases like hello@ or info@.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#E2E8F0]">
                {aliases.map((a) => {
                  const parentDomain = domains.find(d => d.id === a.domain_id);
                  const canTest = parentDomain && parentDomain.verified;
                  return (
                  <li key={a.id} data-testid={`alias-row-${a.alias}-${a.domain}`} className="p-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#0F172A] mono text-sm truncate">{a.alias}@{a.domain}</div>
                        <div className="text-xs text-[#475569] mt-1 truncate">→ {a.destinations.join(", ")}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                        {canTest && (
                          <>
                            <button
                              onClick={() => setSendAsAlias(a)}
                              data-testid={`alias-sendas-${a.alias}`}
                              className="text-xs font-semibold text-[#0F172A] bg-slate-100 px-3 py-1.5 rounded-lg hover:bg-slate-200 inline-flex items-center gap-1">
                              <Mail className="h-3 w-3" /> Send as
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const { data } = await api.post(`/aliases/${a.id}/test-email`);
                                  toast.success(`Test sent to ${data.sent_to} → ${data.forwards_to}. Check your inbox!`);
                                } catch (e) { toast.error(formatApiError(e)); }
                              }}
                              data-testid={`alias-test-email-${a.alias}`}
                              className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg hover:bg-emerald-100">
                              Send test email
                            </button>
                          </>
                        )}
                        <button onClick={() => deleteAlias(a.id)} data-testid={`delete-alias-${a.alias}`}
                          className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                          <Trash2 className="h-4 w-4"/>
                        </button>
                      </div>
                    </div>
                    {canTest && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <AliasStats aliasId={a.id} />
                      </div>
                    )}
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </main>

      <AliasModal open={aliasModal} onClose={() => setAliasModal(false)}
        domains={domains.filter(d => d.verified || user.role === "admin")}
        plan={user.role === "admin" ? "admin" : user.plan}
        aliasCount={aliases.length}
        onCreated={load} />

      <SendAsModal open={!!sendAsAlias} onClose={() => setSendAsAlias(null)} alias={sendAsAlias} />

      <InviteTeammateModal open={inviteModal} onClose={() => setInviteModal(false)} domains={domains} onInvited={load} />

      {/* Extra domain plan-picker modal */}
      <DomainPlanPickerModal
        open={extraDomainModal}
        onClose={() => setExtraDomainModal(false)}
        tiers={config?.extra_domain_tiers}
        isAdmin={user.role === "admin"}
        onAdminAdded={onAdminSlotAdded}
      />
    </div>
  );
}


function DomainRow({ d, isAdmin, onVerify, onDelete }) {
  const [open, setOpen] = useState(false);
  const [dns, setDns] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/domains/${d.id}/dns`);
      setDns(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [d.id]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !dns) loadDns();
  };

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copied"); } catch { /* ignore */ }
  };

  const stateLabel = (d.mailgun_state || (d.verified ? "active" : "unverified")).toLowerCase();
  const isActive = stateLabel === "active";
  const tier = d.plan_tier || "starter";
  const TIER_STYLES = {
    starter: "bg-slate-100 text-slate-700",
    growth:  "bg-[#FEE7E1] text-[#F26953]",
    pro:     "bg-[#0F172A] text-white",
  };

  return (
    <li data-testid={`domain-row-${d.domain}`} className="p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-[#0F172A] truncate flex items-center gap-2 flex-wrap">
            <span>{d.domain}</span>
            <span
              data-testid={`domain-tier-badge-${d.domain}`}
              className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TIER_STYLES[tier] || TIER_STYLES.starter}`}
              title={`${tier.charAt(0).toUpperCase()}${tier.slice(1)} tier`}
            >
              {tier}
            </span>
            {isActive && <DeliverabilityBadge domainId={d.id} />}
          </div>
          <div className="text-xs text-[#475569] mt-1 flex items-center gap-3 flex-wrap">
            <span className="capitalize">{d.provider}</span>
            {isActive ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 className="h-3 w-3"/> Active on Mailgun</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-600 font-medium"><AlertCircle className="h-3 w-3"/> Mailgun: {stateLabel} — DNS incomplete</span>
            )}
            {!d.mailgun_enabled && (
              <span className="inline-flex items-center gap-1 text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Demo mode</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {d.mailgun_enabled && (
            <button onClick={toggle} data-testid={`domain-dns-toggle-${d.domain}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F172A] px-3 py-1.5 rounded-lg hover:bg-slate-100">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}/> DNS
            </button>
          )}
          {!isActive && (
            <button onClick={onVerify} data-testid={`verify-domain-${d.domain}`}
              className="text-xs font-semibold text-[#F26953] px-3 py-1.5 rounded-lg hover:bg-[#FEE7E1]">Re-check</button>
          )}
          <button onClick={onDelete} data-testid={`delete-domain-${d.domain}`}
            className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
            <Trash2 className="h-4 w-4"/>
          </button>
        </div>
      </div>

      {open && (
        <div data-testid={`domain-dns-panel-${d.domain}`} className="mt-4 rounded-xl border border-[#E2E8F0] bg-slate-50 p-4 space-y-4">
          {loading && <div className="text-xs text-slate-500">Loading DNS records…</div>}
          {!loading && dns && (
            <>
              {!isActive && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <strong>Mailgun will reject mail with "550 Relaying denied"</strong> until every record below shows <span className="text-emerald-700 font-semibold">valid</span>. Add each record at your DNS host (Dynadot, Cloudflare, GoDaddy, etc.) and re-check. DNS can take 5–15 min to propagate.
                </div>
              )}

              <DnsSection
                title="1. Receiving · MX records"
                subtitle="Points incoming mail to Mailgun"
                records={dns.receiving || []}
                domain={d.domain}
                onCopy={copy}
                idPrefix={`rx-${d.domain}`}
              />
              <DnsSection
                title="2. Authentication · SPF, DKIM & tracking"
                subtitle="Required for Mailgun to mark your domain active"
                records={dns.sending || []}
                domain={d.domain}
                onCopy={copy}
                idPrefix={`tx-${d.domain}`}
              />

              <div className="flex items-center justify-between pt-2">
                <span className="text-[11px] text-slate-400">
                  {d.last_verified_at ? `Last auto-checked ${new Date(d.last_verified_at).toLocaleString()}` : "Not yet auto-checked"}
                </span>
                <button onClick={onVerify} data-testid={`domain-dns-recheck-${d.domain}`}
                  className="text-xs font-semibold bg-[#0F172A] text-white px-3 py-1.5 rounded-lg hover:bg-[#1e293b]">Re-check now</button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function DnsSection({ title, subtitle, records, onCopy, idPrefix }) {
  if (!records || records.length === 0) {
    return (
      <div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{title}</div>
        <div className="text-xs text-slate-400 mt-1">No records returned. Press "Re-check" to refresh.</div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{title}</div>
        <div className="text-[10px] text-slate-400">{subtitle}</div>
      </div>
      <div className="mt-2 space-y-2">
        {records.map((r, i) => {
          // For root-domain records, Mailgun returns an empty name. Most registrars accept "@" or a blank field.
          const hostDisplay = r.name && r.name.trim() ? r.name : "@";
          const hostCopyValue = r.name && r.name.trim() ? r.name : "@";
          return (
          <div key={i} data-testid={`${idPrefix}-${i}`} className="bg-white border border-[#E2E8F0] rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 text-[11px]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-bold text-[#0F172A] uppercase">{r.type}</span>
                {r.priority && <span className="text-slate-400">priority {r.priority}</span>}
              </div>
              {r.valid ? (
                <span className="text-emerald-600 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3"/> valid</span>
              ) : (
                <span className="text-amber-600 font-semibold inline-flex items-center gap-1"><AlertCircle className="h-3 w-3"/> pending</span>
              )}
            </div>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 flex items-center rounded bg-slate-50 border border-[#E2E8F0] overflow-hidden">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 px-2 py-1.5 bg-white border-r border-[#E2E8F0]">Host</span>
                <code className="flex-1 text-[11px] px-2 py-1.5 overflow-x-auto whitespace-nowrap">{hostDisplay}</code>
              </div>
              <button onClick={() => onCopy(hostCopyValue)} data-testid={`${idPrefix}-${i}-copy-host`}
                className="px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] bg-white hover:bg-slate-50 text-slate-500" title="Copy host">
                <Copy className="h-3.5 w-3.5"/>
              </button>
            </div>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 flex items-center rounded bg-slate-50 border border-[#E2E8F0] overflow-hidden">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 px-2 py-1.5 bg-white border-r border-[#E2E8F0]">Value</span>
                <code className="flex-1 text-[11px] px-2 py-1.5 overflow-x-auto whitespace-nowrap">{r.value}</code>
              </div>
              <button onClick={() => onCopy(r.value)} data-testid={`${idPrefix}-${i}-copy`}
                className="px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] bg-white hover:bg-slate-50 text-slate-500" title="Copy value">
                <Copy className="h-3.5 w-3.5"/>
              </button>
            </div>
            {hostDisplay === "@" && (
              <div className="text-[10px] text-slate-400 pl-1">Use <span className="font-mono font-semibold">@</span> (or leave the Host field blank) — this record applies to the root of your domain.</div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
